<template>
  <AppLazySelect
    ref="lazySelect"
    v-model="value"
    :fetcher="fetcher"
    :filters="filters"
    :disabled="disabled"
    eager
    resource="item"
  >
    <template #single-result-leading="{ value: result }">
      <AppAvatar
        v-if="result.avatar"
        :avatar="result.avatar"
        size="speck"
      />
    </template>

    <template #item-leading="{ item }">
      <AppAvatar
        v-if="item.avatar"
        :avatar="item.avatar"
        size="speck"
      />
    </template>
  </AppLazySelect>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { InsightsResource } from '~/resources';
import { InsightsTracking } from '~/mixins';

const ALL_MEMBERS = { id: 'all', name: 'All Members' };

export default {
  name: 'InsightsLazyMembersSelect',
  mixins: [InsightsTracking],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  // Must be declared: without it the parent's @input listener falls through via $attrs onto the
  // root AppLazySelect, which emits the FULL item object - that object then gets persisted as the
  // member_filter preference and crashes the boot-time restore (see isCanonicalMemberFilter).
  emits: ['input'],
  computed: {
    ...mapState({
      stateValue(state) {
        if (state.filters.user_id) {
          const allPossibleUsers = [
            ...((state.items && Object.values(state.items)) || []),
            ...((state.users.users && Object.values(state.users.users)) || []),
          ].flat();
          const found = allPossibleUsers.find(({ id }) => id === Number(state.filters.user_id));

          return found && { ...found, id: `${found.id}_user_id` };
        }

        if (state.filters.team_id && state.teams.teams) {
          const found = Object.values(state.teams.teams).find(({ id }) => id === Number(state.filters.team_id));

          return found && { ...found, id: `${found.id}_team_id` };
        }
      },
      teamSelected: (state) => state.filters.team_id,
      loading: (state) => state.loading,
      organizationId: (state) => state.organization.id,
    }),
    filters() {
      return [
        { id: 'teams', label: 'Members', value: false, selected: !this.teamSelected },
        { id: 'teams', label: 'Teams', value: true, selected: !!this.teamSelected },
      ];
    },
    value: {
      get() {
        return this.stateValue ?? { ...ALL_MEMBERS, id: 'all_user_id' };
      },
      set(value = null) {
        this.$emit('input', value?.id ?? null);
      },
    },
  },
  methods: {
    ...mapActions(['setPaginatedItems']),
    fetcher(payload) {
      return this.fetchMembers(payload);
    },
    fetchMembers(payload) {
      return InsightsResource.members({ organizationId: this.organizationId }, payload, {
        abortReload: true,
      }).then((result) => {
        const isTeams = payload.filters.teams;

        if (!isTeams) {
          this.setPaginatedItems({ items: result.items, page: payload.page });
        }

        if (!isTeams && payload.page === 1) {
          result.items = [ALL_MEMBERS, ...result.items];
        }
        result.items = result.items.map((item) => ({
          ...item,
          id: isTeams ? `${item.id}_team_id` : `${item.id}_user_id`,
        }));

        return result;
      });
    },
  },
};
</script>
